import { Card, GeneralCameraOffIcon, Loader } from '@outdoorsyco/bonfire';
import { clsx } from 'clsx';
import React from 'react';

import { FavoriteButton } from '@/components/common/FavoriteButton';
import ResponsiveImage from '@/components/utility/ResponsiveImage/ResponsiveImage';
import { WishlistModalWrapper } from '@/components/wishlists/WishlistModal';
import { IWishlistingEventData } from '@/services/analytics/listings/types';
import { IRentalTile } from '@/utility/mapSearchResultToTile';

type TListingTileMediaProps = {
  rentalTile: IRentalTile;
  withPriority?: boolean;
  withFetchPriority?: 'high' | 'low' | undefined;
  largeSourceSize?: boolean;
  className?: string;
  wishlistingEventData?: IWishlistingEventData;
};

export const ModularSmallHorizontalListingTileMedia = ({
  rentalTile,
  withPriority = false,
  withFetchPriority,
  largeSourceSize = false,
  className,
  wishlistingEventData,
}: TListingTileMediaProps) => {
  const { loading, photoUrl, isFavorite, photoAlt, favoriteCount } = rentalTile;

  const listingLoader = !!loading && (
    <Loader className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
  );

  const favoriteToggleButton = (
    <WishlistModalWrapper
      isFavorite={isFavorite}
      item={rentalTile}
      itemType="rental"
      wishlistingEventData={wishlistingEventData}
      TriggerComponent={({ isFavorite: isWishlisted, toggleFavorite }) => (
        <FavoriteButton
          isFavorite={isWishlisted}
          favoriteCount={favoriteCount}
          onClick={toggleFavorite}
        />
      )}
    />
  );

  const actionsLayer = (
    <div className={clsx('absolute -top-5 -left-5 flex w-full p-2 gap-2 items-start')}>
      {favoriteToggleButton}
    </div>
  );

  return (
    <Card.Media fullRadius className={clsx('bg-gray-200', className)}>
      {listingLoader}
      {typeof photoUrl === 'string' && (
        <MediaImage
          src={photoUrl}
          alt={photoAlt}
          withPriority={withPriority}
          withFetchPriority={withFetchPriority}
          largeSourceSize={largeSourceSize}
        />
      )}
      {Array.isArray(photoUrl) && !!photoUrl[0] && (
        <MediaImage
          src={photoUrl[0]}
          alt={photoAlt}
          withPriority={withPriority}
          withFetchPriority={withFetchPriority}
          largeSourceSize={largeSourceSize}
        />
      )}
      {actionsLayer}
    </Card.Media>
  );
};

const MediaImage = ({
  src,
  alt,
  withPriority = false,
  withFetchPriority,
  largeSourceSize,
  ...mouseEvents
}: {
  src: string;
  alt?: string;
  withPriority?: boolean;
  withFetchPriority?: 'high' | 'low' | undefined;
  largeSourceSize?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) => {
  return (
    <ResponsiveImage
      src={src}
      alt={alt}
      cropMode="fill"
      role="presentation"
      priority={withPriority}
      withFetchPriority={withFetchPriority}
      sourceSizes={largeSourceSize ? ['landscape572'] : ['landscape375']}
      className="object-cover object-center w-full h-full"
      {...mouseEvents}
      showFallbackComponent
      customFallbackContent={
        <div {...mouseEvents} className="flex items-center justify-center w-full h-full">
          <GeneralCameraOffIcon className="text-3xl" />
        </div>
      }
    />
  );
};
